import type { RestRecord } from '@libs/data-access/entities'
import { usePrevious } from '@libs/ui/ds'
import type { RestItem } from '@spa/constants/RestTypes'
import { RestTypesType } from '@spa/constants/RestTypes'
import Immutable from 'immutable'
import type { Moment } from 'moment'
import moment from 'moment'
import { useState, useEffect, useMemo } from 'react'
import { fetchTheoreticalRestDuration } from 'snap-redux/actions/theoreticalRestDuration'
import { useAuthorizedFeaturePolicies } from 'snap-redux/modules/countryConfigurations/selectors'
import { useActions } from 'snap-redux/utils'

import type { RestFormData } from '../../hooks'
import { getRevaluatedRestDuration } from '../utils'
import type {
  RevaluatedInputData,
  TableRevaluatedData
} from '../utils/revaluation.types'

type UseRevalo = (props: {
  formData: RestFormData
  setRevaluatedInputData: Function
  onSubmitError: (arg: any) => void
  rest: RestRecord
  restTypeFormValue: string
  restDurationOption: string
  revaluatedInputData: RevaluatedInputData
  revaluatedData: TableRevaluatedData[]
  selectedRest: RestItem
  selectedRestType: string
  setFormError: Function
  setRestOption: Function
  setRestCounterOperationDuration: Function
  setFormLoading: Function
  userContractIdFormValue: string
}) => {
  handleResetDefaultRestDuration: Function
  handleRevaluatedInputDataChange: Function
  defaultRestDuration: number
  defaultRestDurationInDays: number
  revaluatedRestDuration: number
  restCanHaveCustomDuration: boolean
}

const actionCreators = {
  fetchTheoreticalRestDuration
}

const DEFAULT = 'default'

export const useRevalo: UseRevalo = ({
  formData,
  setRevaluatedInputData,
  onSubmitError,
  rest,
  restTypeFormValue,
  restDurationOption,
  revaluatedInputData,
  revaluatedData,
  selectedRest,
  selectedRestType,
  setFormError,
  setRestCounterOperationDuration,
  setRestOption,
  setFormLoading,
  userContractIdFormValue
}) => {
  const actions = useActions(actionCreators)

  const { authorizedRestRevaluationDuration } = useAuthorizedFeaturePolicies()

  const { momentStart, momentEnd } = formData
  const startDate = useMemo(() => momentStart.format(), [momentStart])
  const endDate = useMemo(() => momentEnd.format(), [momentEnd])

  const restId = rest.id || null
  const revaluatedRestValues = rest.custom_value || Immutable.List()

  const userContractFormValue = formData.user_contract_ids

  const restCanHaveCustomDuration =
    userContractFormValue.length === 1 &&
    selectedRest &&
    authorizedRestRevaluationDuration
      ? selectedRest.can_have_custom_value
      : false

  const baseParams = useMemo(
    () => ({
      ...(restId && { rest_id: restId }),
      rest_type: restTypeFormValue,
      user_contract_id: userContractIdFormValue
    }),
    [restId, restTypeFormValue, userContractIdFormValue]
  )

  const initRevaluatedRestDuration = revaluatedRestValues.reduce(
    (memo, entry) => {
      // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
      const [, value] = Object.entries(entry.toJS())[0]
      const revaluatedRest = Number(value).toFixed(2)
      return Number(memo) + Number(revaluatedRest)
    },
    0
  )

  const [revaluatedRestDuration, setRevaluatedRestDuration] = useState(
    initRevaluatedRestDuration
  )
  const [defaultRestDuration, setDefaultRestDuration] = useState(0)
  const [defaultRestDurationInDays, setDefaultRestDurationInDays] = useState(0)

  /**
   * Fetch theoretical rest duration for each week in range and set revaluated input data values
   */
  const setRevaluatedInputDataValues = ({
    useDefaultValue
  }: {
    useDefaultValue: boolean
  }) => {
    const weeksInRange = Immutable.List<Moment>(
      moment
        .range(
          moment(startDate),
          moment(endDate).subtract(1, 'seconds').endOf('isoWeek')
        )
        .by('day', { step: 7 })
    )

    const isOnOneWeek = weeksInRange && weeksInRange.size === 1
    // handle half day rest

    if (isOnOneWeek) {
      return actions
        .fetchTheoreticalRestDuration({
          ...baseParams,
          use_default_value: useDefaultValue,
          starts_at: startDate,
          ends_at: endDate,
          ...(baseParams.rest_type === 'rtt' && { duration_unit: 'days' })
        })
        .then(
          ({
            iso_week: isoWeek,
            theoretical_hours: theoreticalValue,
            other_rests_hours: otherRestsDuration
          }) => {
            setRevaluatedInputData({
              [isoWeek]: {
                ...revaluatedInputData[isoWeek],
                value: Number(theoreticalValue).toFixed(2),
                otherRestsDuration
              }
            })
          },
          error => {
            setFormLoading(false)
            setFormError(error)
          }
        )
    }

    const firstWeekIsComplete = revaluatedData?.[0]?.is_complete_week
    const lastWeekIsComplete =
      revaluatedData?.[revaluatedData.length - 1]?.is_complete_week

    const theoreticalWeeksPromises = [
      ...(firstWeekIsComplete
        ? []
        : [
            actions.fetchTheoreticalRestDuration({
              ...baseParams,
              use_default_value: useDefaultValue,
              starts_at: startDate,
              ends_at: moment(startDate).clone().endOf('isoWeek').format(),
              ...(baseParams.rest_type === 'rtt' && { duration_unit: 'days' })
            })
          ]),
      ...(lastWeekIsComplete
        ? []
        : [
            actions.fetchTheoreticalRestDuration({
              ...baseParams,
              use_default_value: useDefaultValue,
              starts_at: moment(endDate).clone().startOf('isoWeek').format(),
              ends_at: endDate,
              ...(baseParams.rest_type === 'rtt' && { duration_unit: 'days' })
            })
          ])
    ]

    Promise.all(theoreticalWeeksPromises).then(theoreticalWeekData => {
      let theoreticalWeekValues = {}

      theoreticalWeekData.forEach(
        ({
          theoretical_hours: theoreticalValue,
          iso_week: isoWeek,
          other_rests_hours: otherRestsDuration
        }) => {
          theoreticalWeekValues = {
            ...theoreticalWeekValues,
            [isoWeek]: {
              ...revaluatedInputData[isoWeek],
              value: Number(theoreticalValue).toFixed(2),
              otherRestsDuration
            }
          }
        }
      )

      return setRevaluatedInputData(theoreticalWeekValues)
    }, onSubmitError)
  }

  /**
   * Fetch default rest duration
   */
  const setDefaultRestDurationValue = () => {
    setFormLoading(true)

    actions
      .fetchTheoreticalRestDuration({
        ...baseParams,
        use_default_value: true,
        starts_at: startDate,
        ends_at: endDate,
        ...(baseParams.rest_type === 'rtt' && { duration_unit: 'days' })
      })
      .then(response => {
        const defaultRestDuration = response.theoretical_hours
        const defaultRestDurationInDays = response.theoretical_duration

        setDefaultRestDuration(
          parseFloat(Number(defaultRestDuration).toFixed(2))
        )
        setDefaultRestDurationInDays(
          parseFloat(Number(defaultRestDurationInDays).toFixed(2))
        )
        setRestCounterOperationDuration(
          parseFloat(Number(defaultRestDuration).toFixed(2))
        )
        setFormLoading(false)
      }, onSubmitError)
  }

  /**
   * If revaluation table input data change, we set the new revaluated rest duration
   */
  const handleRevaluatedInputDataChange = values => {
    const newRevaluatedInputData = revaluatedInputData
    Object.keys(values).forEach(week => {
      newRevaluatedInputData[week] = {
        ...revaluatedInputData[week],
        value: values[week] ?? revaluatedInputData[week].value
      }
    })

    setRevaluatedInputData(newRevaluatedInputData)

    const revaluatedRestDuration = getRevaluatedRestDuration(
      newRevaluatedInputData,
      revaluatedData
    )
    setRevaluatedRestDuration(revaluatedRestDuration)
    setRestCounterOperationDuration(revaluatedRestDuration)
  }

  /**
   * Fetch default rest duration and init the data
   */
  const isUpdating = rest?.get('id')
  useEffect(() => {
    if (isUpdating) {
      setDefaultRestDurationValue()

      const isDefault = restDurationOption === DEFAULT
      setRevaluatedInputDataValues({ useDefaultValue: isDefault })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdating])

  /**
   * Reset default rest duration and reinit revaluated data values
   */
  const handleResetDefaultRestDuration = () => {
    setRestOption(DEFAULT)
    setDefaultRestDurationValue()
    setRevaluatedInputDataValues({ useDefaultValue: true })
  }

  /**
   * If Rest startDate or endDate change and selected rest is day type and can have custom duration, we reset default rest duration
   * OR
   * If selected rest is day type and can have custom duration, we fetch its default rest duration
   */
  const prevStartDate = usePrevious(startDate)
  const prevEndDate = usePrevious(endDate)
  useEffect(() => {
    if (
      userContractIdFormValue &&
      prevStartDate &&
      prevEndDate &&
      Boolean(selectedRestType) &&
      selectedRestType === RestTypesType.DAY &&
      restCanHaveCustomDuration
    ) {
      handleResetDefaultRestDuration()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate, startDate, selectedRestType, restCanHaveCustomDuration])

  return {
    handleResetDefaultRestDuration,
    handleRevaluatedInputDataChange,
    defaultRestDuration,
    defaultRestDurationInDays,
    revaluatedRestDuration,
    restCanHaveCustomDuration
  }
}
